import React from 'react';
import { Snackbar, Alert, AlertTitle } from '@mui/material';
import { useCallback, useMemo, useState } from 'react';

interface SuccessOrErrorSnackbarProps {
    open: boolean;
    onClose: () => void;

    // Determines success or error state
    success: boolean;

    // Titles/messages (same idea as your dialog)
    successTitle: string;
    successMessage: string;

    errorTitle: string;
    errorMessage: string;

    // Optional behavior
    autoHideDuration?: number; // default 2500ms
    anchorOrigin?: {
        vertical: 'top' | 'bottom';
        horizontal: 'left' | 'center' | 'right';
    };
}

const SuccessOrErrorSnackbar: React.FC<SuccessOrErrorSnackbarProps> = ({
    open,
    onClose,
    success,
    successTitle,
    successMessage,
    errorTitle,
    errorMessage,
    autoHideDuration = 2500,
    anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
}) => {
    const title = success ? successTitle : errorTitle;
    const message = success ? successMessage : errorMessage;

    // IMPORTANT: MUI Snackbar fires onClose for "clickaway" — usually you want to ignore it
    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        onClose();
    };

    return (
        <Snackbar
            open={open}
            onClose={handleClose}
            autoHideDuration={autoHideDuration}
            anchorOrigin={anchorOrigin}
        >
            <Alert
                onClose={handleClose}
                severity={success ? 'success' : 'error'}
                variant="filled"
                sx={{ width: '100%' }}
            >
                {!!title && <AlertTitle>{title}</AlertTitle>}
                {message}
            </Alert>
        </Snackbar>
    );
};


type ResultType = 'success' | 'error';

interface UseResultSnackbarOptions {
    successTitle?: string;
    errorTitle?: string;
    autoHideDuration?: number;
    anchorOrigin?: {
        vertical: 'top' | 'bottom';
        horizontal: 'left' | 'center' | 'right';
    };
}

export function useResultSnackbar(options: UseResultSnackbarOptions = {}) {
    const {
        successTitle = 'Success',
        errorTitle = 'Error',
        autoHideDuration = 2500,
        anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
    } = options;

    const [open, setOpen] = useState(false);
    const [type, setType] = useState<ResultType>('success');
    const [message, setMessage] = useState('');

    const showSuccess = useCallback((msg: string) => {
        setType('success');
        setMessage(msg);
        setOpen(true);
    }, []);

    const showError = useCallback((msg: string) => {
        setType('error');
        setMessage(msg);
        setOpen(true);
    }, []);

    const close = useCallback(() => {
        setOpen(false);
    }, []);

    const snackbar = useMemo(
        () => (
            <SuccessOrErrorSnackbar
                open={open}
                onClose={close}
                success={type === 'success'}
                successTitle={successTitle}
                successMessage={message}
                errorTitle={errorTitle}
                errorMessage={message}
                autoHideDuration={autoHideDuration}
                anchorOrigin={anchorOrigin}
            />
        ),
        [open, close, type, message, successTitle, errorTitle, autoHideDuration, anchorOrigin]
    );

    return { showSuccess, showError, snackbar, close };
}
